import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button/Button";
import CreateWorkerTemplateSelect from "@/components/worker/create/CreateWorkerTemplateSelect";
import CreateWorkerDeploy from "@/components/worker/create/CreateWorkerDeploy";
import CreateWorkerCapabilities from "@/components/worker/create/CreateWorkerCapabilities";
import CreateWorkerTest from "@/components/worker/create/CreateWorkerTest";
import CreateWorkerCompletion from "@/components/worker/create/CreateWorkerCompletion";
import { PageLayout } from "@/components/PageLayout";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  WorkerCreationProvider,
  useWorkerCreation,
} from "@/context/WorkerCreationContext";
import { useWorkerList } from "@/context/WorkerListContext";
import { Stepper } from "@/components/common/Stepper";
import { WorkerAniAPI } from "@/services/api";
import type {
  DeployWorkerPayload,
  WorkerTemplate,
} from "@/services/api/worker-ani/types";

export const meta = () => {
  return [
    { title: "Create Worker | Ani Tech" },
    { name: "description", content: "Create a new AI worker" },
  ];
};
import { LoadingIndicator } from "@/components/indicators/LoadingIndicator";
import { useAuth } from "@/context/AuthContext";

// Step definitions for create vs edit flows
const createSteps = [
  { id: "Step 1", name: "Select Template" },
  { id: "Step 2", name: "Worker Setup" },
  { id: "Step 3", name: "Add Capabilities" },
  { id: "Step 4", name: "Test Worker" },
  { id: "Step 5", name: "Complete" },
];

const editSteps = [
  { id: "Step 1", name: "Worker Setup" },
  { id: "Step 2", name: "Add Capabilities" },
  { id: "Step 3", name: "Test Worker" },
  { id: "Step 4", name: "Complete" },
];

function StepContent({
  step,
  nextStep,
  isEditMode,
}: {
  step: number;
  nextStep: () => void;
  isEditMode: boolean;
}) {
  const { selectedTemplate, setSelectedTemplate } = useWorkerCreation();

  if (isEditMode) {
    // Edit flow: no template selection
    switch (step) {
      case 0:
        return <CreateWorkerDeploy isEditMode={isEditMode} />;
      case 1:
        return <CreateWorkerCapabilities />;
      case 2:
        return <CreateWorkerTest />;
      case 3:
        return <CreateWorkerCompletion />;
      default:
        return <div>Step {step + 1} Content</div>;
    }
  }

  // Create flow
  switch (step) {
    case 0:
      return (
        <CreateWorkerTemplateSelect
          selectedTemplate={selectedTemplate}
          onSelect={setSelectedTemplate}
          onNext={nextStep}
        />
      );
    case 1:
      return <CreateWorkerDeploy isEditMode={isEditMode} />;
    case 2:
      return <CreateWorkerCapabilities />;
    case 3:
      return <CreateWorkerTest />;
    case 4:
      return <CreateWorkerCompletion />;
    default:
      return <div>Step {step + 1} Content</div>;
  }
}

function CreateWorkerStepper() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const {
    workerData,
    updateWorkerData,
    saveWorkerToAPI,
    selectedTemplate,
    setSelectedTemplate,
    clearStaleData,
    resetWorkerData,
  } = useWorkerCreation();
  const { updateWorker, addWorker, refreshPinnedWorkers } = useWorkerList();
  const [isDeploying, setIsDeploying] = useState(false);
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  const [searchParams] = useSearchParams();
  const workerId = searchParams.get("workerId");
  const isEditMode = !!workerId;
  const { user } = useAuth();

  // Clear stale data when starting a fresh creation (not edit mode)
  useEffect(() => {
    if (!isEditMode) {
      // Clear localStorage and reset form for fresh creation
      localStorage.removeItem("worker-creation-data");
      localStorage.removeItem("worker-creation-template");
      clearStaleData();
    }
  }, [isEditMode, clearStaleData]);

  // Reset worker creation data when component unmounts (user navigates away)
  useEffect(() => {
    return () => {
      if (!isEditMode) {
        resetWorkerData();
      }
    };
  }, [isEditMode, resetWorkerData]);

  useEffect(() => {
    if (isEditMode && workerId) {
      const fetchWorkerDetails = async () => {
        try {
          const worker = await WorkerAniAPI.getWorkerCard(workerId);

          if (!worker.is_editable) {
            navigate("/worker/directory");
            return;
          }

          const templates = await WorkerAniAPI.getWorkerTemplates();
          const matchingTemplate =
            templates.find((t) => t._id === worker.template_id) || null;

          updateWorkerData({
            _id: worker._id,
            name: worker.name,
            jobTitle: worker.job_title,
            bio: worker.bio,
            instructions: (worker as any).instructions || "",
            rules: worker.rules?.join("\n"),
            selectedCapabilities: new Set(worker.capabilities || []),
          });
          setSelectedTemplate(matchingTemplate, { preserveData: true });
        } catch (error) {
          console.error("Failed to fetch worker details for editing:", error);
        }
      };
      fetchWorkerDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerId, isEditMode, user, navigate]);

  const stepsToShow = isEditMode ? editSteps : createSteps;

  const nextStep = () => {
    if (currentStep < stepsToShow.length - 1) {
      setCurrentStep(currentStep + 1);
    } else if (currentStep === stepsToShow.length - 1) {
      // Reset worker creation data when navigating away from completion
      if (!isEditMode) {
        resetWorkerData();
      }
      navigate("/worker/directory");
    }
  };

  const createDraftWorker = async () => {
    if (isEditMode || workerData._id) {
      // If we're in edit mode or already have a worker ID, just proceed
      return;
    }

    setIsCreatingDraft(true);
    try {
      const payload: DeployWorkerPayload = {
        template_id: selectedTemplate?._id ?? null,
        name: workerData.name,
        job_title: workerData.jobTitle,
        bio: workerData.bio,
        instructions: workerData.instructions,
        rules: (workerData.rules || "").split("\n").filter(Boolean),
        capabilities: Array.from(workerData.selectedCapabilities),
      };

      const draftWorker = await WorkerAniAPI.createDraftWorker(payload);
      console.log("Draft worker created:", draftWorker._id);

      // Update worker creation context with the draft ID
      updateWorkerData({ _id: draftWorker._id });

      // Add draft worker to navigation context
      addWorker(draftWorker);
    } catch (error) {
      console.error("Failed to create draft worker:", error);
      // Continue with the flow even if draft creation fails
    } finally {
      setIsCreatingDraft(false);
    }
  };

  const handleNextStep = async () => {
    // If we're moving from Step 2 (Worker Setup) to Step 3 (Add Capabilities)
    // and we don't have a worker ID yet, create a draft worker
    if (currentStep === 1 && !isEditMode && !workerData._id) {
      await createDraftWorker();
    } else if (workerData._id) {
      // If we have a worker ID, save current changes to the API
      setIsDeploying(true);
      try {
        await saveWorkerToAPI();
      } catch (error) {
        console.error("Failed to save worker data to API:", error);
        // Continue with the flow even if save fails
      } finally {
        setIsDeploying(false);
      }
    }

    nextStep();
  };

  const prevStep = () => {
    if (currentStep === 0) {
      // Reset worker creation data when canceling
      if (!isEditMode) {
        resetWorkerData();
      }
      navigate("/worker/directory");
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleDeployOrUpdate = async () => {
    setIsDeploying(true);
    try {
      const payload: DeployWorkerPayload = {
        template_id: selectedTemplate?._id ?? null,
        name: workerData.name,
        job_title: workerData.jobTitle,
        bio: workerData.bio,
        instructions: workerData.instructions,
        rules: (workerData.rules || "").split("\n").filter(Boolean),
        capabilities: Array.from(workerData.selectedCapabilities),
        status: "active", // Set status to active
      };

      let deployedWorkerId: string;

      if (isEditMode) {
        const updatedWorker = await WorkerAniAPI.updateWorker(
          workerId,
          payload
        );

        // Update worker in navigation context
        updateWorker(workerId, updatedWorker);
        deployedWorkerId = updatedWorker._id;
      } else if (workerData._id) {
        // We have a draft worker, update it to deployed status
        const updatedWorker = await WorkerAniAPI.updateWorker(
          workerData._id,
          payload
        );

        // Update worker in navigation context
        updateWorker(workerData._id, updatedWorker);
        deployedWorkerId = updatedWorker._id;
      } else {
        // Create new worker (fallback)
        const newWorker = await WorkerAniAPI.deployWorker(payload);
        // Update worker creation context with the ID
        updateWorkerData({ _id: newWorker._id });
        // Add new worker to navigation context
        addWorker(newWorker);
        deployedWorkerId = newWorker._id;
      }

      // Auto-pin the newly deployed worker for easy access
      if (!isEditMode && deployedWorkerId) {
        try {
          await WorkerAniAPI.pinWorker(deployedWorkerId);
          console.log("Worker automatically pinned:", deployedWorkerId);

          // Refresh the pinned workers list in the navigation
          await refreshPinnedWorkers();
        } catch (error) {
          console.error("Failed to pin worker:", error);
          // Don't break the flow if pinning fails
        }
      }

      nextStep();
    } catch (error) {
      console.error("Failed to deploy worker:", error);
      // You can also show an error message to the user
    } finally {
      setIsDeploying(false);
    }
  };

  const handleNextAction = () => {
    // Trigger deploy/update on the second-to-last step (Test Worker)
    if (currentStep === stepsToShow.length - 2) {
      handleDeployOrUpdate();
    } else {
      handleNextStep();
    }
  };

  return (
    <PageLayout
      title={isEditMode ? "Edit Worker" : "Create worker"}
      subtitle={
        isEditMode
          ? "Update your custom AI worker."
          : "Build and deploy custom AI workers for you and your organization."
      }
    >
      <div className="flex flex-col h-full">
        <div className="mb-4 flex-shrink-0 px-2">
          <Stepper
            steps={stepsToShow}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        </div>

        <div className="mb-4 px-2 flex justify-between flex-shrink-0">
          <Button onClick={prevStep} className="px-3 py-1 text-sm">
            {currentStep === 0 ? "Cancel" : "Previous"}
          </Button>
          {currentStep < stepsToShow.length - 1 && (
            <Button
              onClick={handleNextAction}
              color="indigo"
              className="px-3 py-1 text-sm"
              disabled={isDeploying || isCreatingDraft}
            >
              {(isDeploying || isCreatingDraft) && (
                <LoadingIndicator className="h-5 w-5 mr-2" />
              )}
              {currentStep === stepsToShow.length - 2
                ? isEditMode
                  ? "Update"
                  : "Deploy"
                : "Next"}
            </Button>
          )}
        </div>

        <div className=" rounded-2xl shadow-lg p-4 flex-grow overflow-y-auto min-h-0 bg-gradient-to-bl from-slate-700/50 to-slate-700/40">
          <StepContent
            step={currentStep}
            nextStep={nextStep}
            isEditMode={isEditMode}
          />
        </div>
      </div>
    </PageLayout>
  );
}

export default function WorkerCreateRoute() {
  return (
    <WorkerCreationProvider>
      <CreateWorkerStepper />
    </WorkerCreationProvider>
  );
}
